import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, ImageIcon, Video, Zap, ArrowRight, LayoutGrid, Palette, Mic, Menu, X,
  BookOpen, Scissors, Wand2, Film, FolderOpen, Music, Monitor, Type, Sun, Layers,
  Smartphone, Crop, Package, Users, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { MarketingFooter } from "@/components/MarketingShell";
import { AdUnit } from "@/components/AdUnit";
import { RewardedAdCard } from "@/components/RewardedAdCard";
import { SoroBlog } from "@/components/SoroBlog";
import { USE_CASES } from "@/data/useCases";

const POPULAR_SLUGS = [
  "ai-image-generator", "ai-video-ad-maker", "website-to-video-ad", "ai-ugc-ad-maker",
  "ai-photo-editor", "ai-voiceover-generator", "ai-youtube-thumbnail-maker", "ai-reels-maker",
  "ai-music-generator", "nox-ai-vs-canva",
];
const POPULAR_USE_CASES = POPULAR_SLUGS
  .map((s) => USE_CASES.find((u) => u.slug === s))
  .filter(Boolean);

const LANDING_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://noxaiapp.com/#organization",
      name: "Nox AI",
      url: "https://noxaiapp.com",
      logo: "https://noxaiapp.com/nox-logo.png",
      description: "AI creative platform for everyone — generate images, video, voiceovers, music and finished content in one all-in-one AI studio.",
    },
    {
      "@type": "WebSite",
      "@id": "https://noxaiapp.com/#website",
      url: "https://noxaiapp.com",
      name: "Nox AI",
      publisher: { "@id": "https://noxaiapp.com/#organization" },
    },
    {
      "@type": "SoftwareApplication",
      name: "Nox AI",
      applicationCategory: "MultimediaApplication",
      operatingSystem: "Web",
      description:
        "Create images (FLUX, Ideogram, Grok, Nano Banana), video (Kling, Veo, Grok), voiceovers, music, art and talking avatars — your all-in-one AI creative studio.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      featureList: [
        "AI image generation",
        "AI video generation",
        "AI voiceover & music",
        "AI talking avatars",
        "AI art & image editing",
        "Content for social, YouTube & more",
      ],
    },
  ],
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HERO_BG =
  "https://images.pexels.com/photos/18337643/pexels-photo-18337643.jpeg?auto=compress&cs=tinysrgb&w=1600";

const SHOWCASE = [
  {
    src: "/showcase/flux-sneaker.webp",
    type: "image",
    tool: "FLUX 1.1 Pro",
    desc: "Neon sneaker product ad",
  },
  {
    src: "/showcase/ideogram-poster.webp",
    type: "image",
    tool: "Ideogram v3",
    desc: "Energy-drink poster — real, crisp text",
  },
  {
    src: "/showcase/grok-perfume.webp",
    type: "image",
    tool: "Grok Imagine",
    desc: "Luxury perfume hero shot",
  },
  {
    src: "/showcase/nano-watch.webp",
    type: "image",
    tool: "Nano Banana Pro",
    desc: "Photoreal watch photography",
  },
  {
    src: "/showcase/kling-dog.mp4",
    poster: "/showcase/kling-dog.webp",
    type: "video",
    tool: "Kling Video",
    desc: "Cinematic motion clip — brand storytelling",
    span: "col-span-2 row-span-1",
  },
  {
    src: "/showcase/ad-studio-headphones.webp",
    type: "image",
    tool: "Ad Studio · Bria",
    desc: "Product dropped into a lifestyle scene",
  },
  {
    src: "/showcase/grok-i2v-product.mp4",
    poster: "/showcase/grok-i2v-product.webp",
    type: "video",
    tool: "Grok Image→Video",
    desc: "A still product photo, brought to life",
  },
  {
    src: "/showcase/veo3-supercar.mp4",
    poster: "/showcase/veo3-supercar.webp",
    type: "video",
    tool: "Veo 3",
    desc: "Premium 1080p cinematic ad — Google Veo 3",
    span: "col-span-2 row-span-1",
  },
  {
    src: "story-excerpt",
    type: "text",
    tool: "AI Story Writer · Claude",
    desc: "A full story from one idea — consistent characters & plot",
    span: "col-span-2 row-span-1",
  },
  // ─── Editing row ─────────────────────────────────────────────────────────
  {
    src: "editing-suite-tile",
    type: "feature-edit",
    tool: "Video Editing Suite",
    desc: "7 AI editing tools — all in one place",
    span: "col-span-2 row-span-1",
  },
  {
    src: "https://images.unsplash.com/photo-1608699565424-597c238383c2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    type: "image",
    tool: "AI Relight",
    desc: "Dramatic lighting changes from a single text prompt",
  },
  {
    src: "https://images.unsplash.com/photo-1762028895582-1d0b4c3822c6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    type: "image",
    tool: "Storyboard Studio",
    desc: "Script → scenes → assembled multi-scene final video",
  },
  // ─── Manual editor + storyboard row ──────────────────────────────────────
  {
    src: "manual-editor-tile",
    type: "feature-manual",
    tool: "Manual Photo Editor",
    desc: "Photoshop-lite tools built into Nox AI — always free",
    span: "col-span-2 row-span-1",
  },
  {
    src: "storyboard-flow-tile",
    type: "feature-storyboard",
    tool: "AI Storyboard",
    desc: "One click from script to final assembled video",
    span: "col-span-2 row-span-1",
  },
];

const STORY_SAMPLES = [
  {
    genre: "Thriller",
    title: "The Midnight Ledger",
    excerpt: "The safe clicked open on the third try, and Dana finally understood why her brother had never come home. The ledger inside wasn't full of money — it was full of names. Hers was on the last page, circled twice in red.",
  },
  {
    genre: "Children's",
    title: "Pip and the Paper Moon",
    excerpt: "Pip folded the moon from a single sheet of silver paper and set it gently on the windowsill. That night, it began to glow — softly at first, then bright enough to light the whole sleepy village below.",
  },
  {
    genre: "Sci-Fi",
    title: "The Last Signal",
    excerpt: "Forty light-years from home, the antenna caught it again: three tones, then silence, then three tones. Commander Voss held her breath. After a century of listening, something out there had finally decided to answer.",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const ctaTarget = user ? "/studio" : "/register";
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-white overflow-x-hidden">
      <Seo
        title="Your all-in-one AI creative studio"
        description="Create images, videos, voiceovers, music, art and more from a single prompt. Nox AI is your all-in-one AI creative studio — for creators, businesses and anyone with an idea. Powered by FLUX, Kling, Veo, Ideogram & more."
        path="/"
        jsonLd={LANDING_JSONLD}
      />
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-30 bg-[#050508]/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/nox-logo.png" alt="Nox AI" className="h-9 w-9 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="font-heading font-black text-lg tracking-tight">
                Nox<span className="text-primary"> AI</span>
              </span>
              <span className="hidden sm:block text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                All-in-one AI creative studio
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/use-cases" data-testid="nav-usecases-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">
                Use cases
              </Button>
            </Link>
            <Link to="/pricing" data-testid="nav-pricing-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">
                Pricing
              </Button>
            </Link>
            <Link to="/about" data-testid="nav-about-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">
                About
              </Button>
            </Link>
            <Link to="/blog" data-testid="nav-blog-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">
                Blog
              </Button>
            </Link>
            <Link to="/contact" data-testid="nav-contact-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">
                Contact
              </Button>
            </Link>
            <Link to="/login" data-testid="nav-login-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">
                Sign in
              </Button>
            </Link>
            <Link to="/register" data-testid="nav-getstarted-link">
              <Button className="bg-primary text-black hover:bg-[#00C2D9] font-semibold rounded-full px-4 sm:px-5 h-9">
                Get started
              </Button>
            </Link>
            {/* Mobile menu toggle */}
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
              data-testid="mobile-menu-toggle"
              className="sm:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg border border-white/10 text-white/90 hover:bg-white/5 transition-colors"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              data-testid="mobile-menu"
              className="sm:hidden overflow-hidden border-t border-white/10 bg-[#050508]/95 backdrop-blur-xl"
            >
              <div className="px-6 py-3 flex flex-col">
                {[
                  { to: "/use-cases", label: "Use cases", testid: "mobile-nav-usecases" },
                  { to: "/pricing", label: "Pricing", testid: "mobile-nav-pricing" },
                  { to: "/about", label: "About", testid: "mobile-nav-about" },
                  { to: "/blog", label: "Blog", testid: "mobile-nav-blog" },
                  { to: "/contact", label: "Contact", testid: "mobile-nav-contact" },
                  { to: "/login", label: "Sign in", testid: "mobile-nav-login" },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    onClick={() => setMenuOpen(false)}
                    className="py-3 text-base font-medium text-white/80 hover:text-white border-b border-white/5 last:border-0 transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      {/* Hero */}
      <section className="relative pt-28 sm:pt-40 pb-20 sm:pb-28 px-6">
        <div
          className="absolute inset-0 z-0 opacity-40"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to bottom, black 20%, transparent 95%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 20%, transparent 95%)",
          }}
        />
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-transparent via-background/40 to-background" />

        <div className="relative z-10 max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="show"
                className="text-xs tracking-[0.25em] uppercase font-bold text-primary mb-6"
              >
                <span className="text-glow">/// Nox AI · Your all-in-one AI creative studio</span>
              </motion.p>
              <motion.h1
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={1}
                className="font-heading text-4xl sm:text-6xl lg:text-7xl tracking-tighter font-black leading-[1.02] sm:leading-[0.95]"
              >
                Create{" "}
                <span className="text-primary text-glow">anything</span> in seconds.
              </motion.h1>
              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={2}
                className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
              >
                Nox AI turns a single prompt into images, videos, voiceovers,
                music <span className="text-white/90">and more</span> — one creative studio for
                creators, businesses and anyone with an idea.
              </motion.p>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={3}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link to={ctaTarget} data-testid="hero-cta-button">
                  <Button className="bg-primary text-black hover:bg-[#00C2D9] font-semibold rounded-full px-7 h-12 text-base group">
                    Start creating
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Zap className="h-4 w-4 text-primary" />
                  FLUX · Grok Imagine · Kling · ElevenLabs
                </div>
              </motion.div>
            </div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="md:col-span-4"
            >
              <div className="rounded-2xl border border-white/10 bg-[#0F1015] p-6 glow-cyan">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">
                  Live stats
                </div>
                <div className="space-y-5">
                  {[
                    { k: "Avg. image time", v: "~2s" },
                    { k: "Formats", v: "Square · 16:9 · 9:16" },
                    { k: "Output", v: "Images · Video · Voice · Stories" },
                  ].map((s) => (
                    <div key={s.k} className="flex items-baseline justify-between border-b border-white/5 pb-3">
                      <span className="text-sm text-muted-foreground">{s.k}</span>
                      <span className="font-mono font-semibold text-primary">{s.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="relative z-10 px-6 pb-20 sm:pb-28">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <FeatureCard
              className="md:col-span-7"
              icon={ImageIcon}
              title="AI Image Generator"
              desc="Prompt it. Pick your aspect ratio and batch count. Get crisp, high-resolution visuals with FLUX, Grok, Ideogram (real text) & Nano Banana Pro — perfect for social posts, art, thumbnails, banners and product shots."
            />
            <FeatureCard
              className="md:col-span-5"
              icon={Video}
              title="Video for YouTube & Social"
              desc="Turn a line of copy into motion with Kling & Grok. Choose 16:9, 9:16 or 1:1, or animate a product shot with Image-to-Video."
            />
            <FeatureCard
              className="md:col-span-3"
              icon={Mic}
              title="AI Voiceovers & Audio"
              desc="Add studio-quality narration to any video with ElevenLabs voices — then merge the voiceover straight onto your video in one click."
            />
            <FeatureCard
              className="md:col-span-3"
              icon={BookOpen}
              title="AI Story Writer"
              desc="Turn an idea into a full story. Claude builds a Story Bible and writes with it — so characters, world and plot stay consistent chapter after chapter."
            />
            <FeatureCard
              className="md:col-span-3"
              icon={Palette}
              title="On-brand, every time"
              desc="Save brand kits with your logo & colors, auto-composited onto every generation so your content always stays consistent."
            />
            <FeatureCard
              className="md:col-span-3"
              icon={LayoutGrid}
              title="Your entire creative library"
              desc="Every image, video and voiceover is saved to a searchable, manageable gallery — revisit, filter, download or bulk-delete anytime."
            />
            <FeatureCard
              className="md:col-span-6"
              icon={Zap}
              title="Ad Studio & Autonomous Ads"
              desc="Drop any product image into a lifestyle scene with Ad Studio. Or paste a URL — Autonomous Ads scans your page and generates 8 polished ad concepts with copy, imagery and a 30-second video spot, ready to deploy."
            />
            <FeatureCard
              className="md:col-span-6"
              icon={Scissors}
              title="Edit & polish everything"
              desc="A full suite of AI editing tools: upscale video 4×, burn auto-captions, reframe 16:9 to vertical Reels, AI relight images, remove backgrounds, run lip sync, or mix voiceover + music into a finished ad — all without leaving Nox AI."
            />
          </div>
        </div>
      </section>

      {/* Proof of work — real outputs made with Nox AI */}
      <section className="relative z-10 px-6 pb-20 sm:pb-28" data-testid="showcase-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-10 text-center">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-primary mb-3">
              /// Made with Nox AI
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
              Real outputs. Real tools. No cherry-picking.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Every asset below was generated in Nox AI — each tagged with the exact
              engine that produced it.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[220px] sm:auto-rows-[280px]">
            {SHOWCASE.map((item, i) => (
              <motion.div
                key={item.src}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`showcase-item-${i}`}
                className={`group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0F1015] ${item.span || ""}`}
              >
                {item.type === "text" ? (
                  <ShowcaseStory tool={item.tool} desc={item.desc} />
                ) : item.type === "feature-edit" ? (
                  <ShowcaseEditingSuite tool={item.tool} desc={item.desc} />
                ) : item.type === "feature-manual" ? (
                  <ShowcaseManualEditor tool={item.tool} desc={item.desc} />
                ) : item.type === "feature-storyboard" ? (
                  <ShowcaseStoryboard tool={item.tool} desc={item.desc} />
                ) : (
                  <>
                    {item.type === "video" ? (
                      <ShowcaseVideo src={item.src} poster={item.poster} />
                    ) : (
                      <img
                        src={item.src}
                        alt={item.desc}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                    <div className="absolute left-3 top-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary border border-primary/30">
                        {item.type === "video" ? <Video className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                        {item.tool}
                      </span>
                    </div>
                    <div className="absolute inset-x-3 bottom-3">
                      <p className="text-sm font-semibold text-white leading-tight">{item.desc}</p>
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Complete Toolkit Section */}
      <section className="relative z-10 px-6 pb-20 sm:pb-28" data-testid="toolkit-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-primary mb-3">Full feature set</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
              Everything in one studio
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              No more juggling five different subscriptions — every tool you need to create, edit and
              publish is right here.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ToolkitCategory
              icon={Sparkles}
              label="Generate"
              accent="text-primary"
              border="border-primary/20"
              bg="bg-primary/5"
              tools={[
                { name: "AI Images", detail: "FLUX 1.1 Pro · Ideogram v3 · Grok · Nano Banana Pro" },
                { name: "AI Video", detail: "Kling · Veo 3 · Grok · Seedance — text-to-video & i2v" },
                { name: "AI Voiceover", detail: "ElevenLabs v3 — 21 voices, 2 quality tiers" },
                { name: "AI Music", detail: "Google Lyria 3.5 — full tracks with lyrics" },
                { name: "Talking Avatars", detail: "Kling AI Avatar — studio-quality lip sync" },
                { name: "AI Story Writer", detail: "Claude — story bible + consistent chapters" },
                { name: "Storyboard", detail: "Script → scenes → assembled multi-scene video" },
                { name: "Ad Studio", detail: "Product photography & lifestyle scene staging" },
                { name: "Autonomous Ads", detail: "URL scan → 8 polished ad concepts + 30s video" },
              ]}
            />
            <ToolkitCategory
              icon={Scissors}
              label="Edit"
              accent="text-violet-400"
              border="border-violet-400/20"
              bg="bg-violet-400/5"
              tools={[
                { name: "Manual Photo Editor", detail: "Photoshop-lite — crop, annotate, filter, resize" },
                { name: "AI Relight", detail: "FLUX Kontext — change lighting from a text prompt" },
                { name: "Auto-Captions", detail: "Wizper — transcribe & burn subtitles onto video" },
                { name: "Video Upscale", detail: "Real-ESRGAN + Topaz Pro — 2× or 4× HD upscale" },
                { name: "Background Removal", detail: "Clean cutout from images and full video clips" },
                { name: "Reframe for Reels", detail: "AI inpaint 16:9 footage → perfect 9:16 vertical" },
                { name: "Lip Sync", detail: "Replace audio and sync mouth movement" },
                { name: "Assemble Ad", detail: "Captions + music mix → finished MP4 in one click" },
              ]}
            />
            <ToolkitCategory
              icon={FolderOpen}
              label="Organise"
              accent="text-amber-400"
              border="border-amber-400/20"
              bg="bg-amber-400/5"
              tools={[
                { name: "Brand Kits", detail: "Multi-image logo compositing onto every generation" },
                { name: "Campaigns", detail: "Bundle assets, download ZIP, share with a link" },
                { name: "Public Share Links", detail: "Unauthenticated campaign view for clients" },
                { name: "Social Auto-post", detail: "Schedule & publish directly to Instagram + more" },
                { name: "Gallery", detail: "Filter by type, lightbox preview, bulk delete" },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="relative z-10 px-6 pb-20 sm:pb-28" data-testid="popular-usecases-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-primary text-sm font-semibold tracking-[0.2em] uppercase mb-2">Explore</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Popular use cases</h2>
            </div>
            <Link to="/use-cases" data-testid="popular-usecases-viewall" className="text-sm font-semibold text-primary hover:text-[#00C2D9] inline-flex items-center gap-1">
              View all use cases <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {POPULAR_USE_CASES.map((u) => (
              <Link
                key={u.slug}
                to={`/use-cases/${u.slug}`}
                data-testid={`popular-usecase-${u.slug}`}
                className="group flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#0F1015] px-4 py-3.5 hover:border-primary/40 hover:bg-[#12141c] transition-colors"
              >
                <div className="min-w-0">
                  <span className="block text-sm font-semibold text-white/90 group-hover:text-white truncate">{u.title}</span>
                  {u.models?.[0] && (
                    <span className="mt-0.5 block text-xs text-muted-foreground truncate">
                      Best: <span className="text-primary/90 font-medium">{u.models[0].name}</span>
                    </span>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 px-6 pb-20 sm:pb-28" data-testid="blog-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
              From the Nox AI blog
            </h2>
            <p className="mt-3 text-muted-foreground max-w-lg mx-auto">
              Guides, ideas and inspiration to help you create standout content.
            </p>
          </div>
          <SoroBlog limit={3} showSearch={false} />
          <div className="text-center mt-8">
            <Link
              to="/blog"
              data-testid="landing-blog-viewall"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-[#00C2D9] transition-colors"
            >
              View all posts →
            </Link>
          </div>
        </div>
      </section>

      <section className="relative z-10 px-6 pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto rounded-3xl border border-primary/20 bg-gradient-to-br from-[#0F1015] to-[#0a0b12] p-8 sm:p-12 md:p-16 text-center glow-cyan">
          <h2 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter">
            Ready to create something amazing?
          </h2>
          <p className="mt-4 text-muted-foreground max-w-lg mx-auto">
            Join creators and businesses using Nox AI to make standout content —
            no design or production skills required.
          </p>
          <Link to={ctaTarget} data-testid="footer-cta-button">
            <Button className="mt-8 bg-primary text-black hover:bg-[#00C2D9] font-semibold rounded-full px-8 h-12 text-base">
              Get started free
            </Button>
          </Link>
        </div>
      </section>

      <section className="px-4 sm:px-6 pb-4">
        <div className="max-w-[1100px] mx-auto space-y-4">
          <RewardedAdCard />
          <AdUnit slot={process.env.REACT_APP_AD_SLOT_LANDING} />
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function ShowcaseStory({ tool, desc }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % STORY_SAMPLES.length), 4500);
    return () => clearInterval(id);
  }, []);
  const s = STORY_SAMPLES[idx];
  return (
    <div className="absolute inset-0 flex flex-col p-5 bg-gradient-to-br from-[#12141c] to-[#080910]" data-testid="showcase-story">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary border border-primary/30">
          <BookOpen className="h-3 w-3" /> {tool}
        </span>
        <AnimatePresence mode="wait">
          <motion.span
            key={`g-${idx}`}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
            className="rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-[10px] font-semibold text-white/70"
          >
            {s.genre}
          </motion.span>
        </AnimatePresence>
      </div>
      <div className="flex-1 min-h-0 mt-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-heading text-lg font-black tracking-tight text-white">{s.title}</p>
            <p className="mt-2 text-[13px] leading-relaxed text-white/70 italic overflow-hidden line-clamp-3 sm:line-clamp-4">
              “{s.excerpt}”
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-between gap-2 pt-3">
        <p className="text-sm font-semibold text-white leading-tight">{desc}</p>
        <div className="flex gap-1 shrink-0">
          {STORY_SAMPLES.map((_, i) => (
            <span key={i} className={`h-1.5 w-1.5 rounded-full transition-colors ${i === idx ? "bg-primary" : "bg-white/20"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ShowcaseVideo({ src, poster }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="absolute inset-0">
      {inView ? (
        <video
          src={src}
          poster={poster}
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        poster && (
          <img
            src={poster}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
          />
        )
      )}
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`group rounded-2xl border border-[#222430] bg-[#0F1015] p-8 transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_8px_32px_rgba(0,229,255,0.06)] ${className}`}
    >
      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
        <Icon className="h-6 w-6 text-primary" />
      </div>
      <h3 className="font-heading text-xl font-bold tracking-tight mb-3">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{desc}</p>
    </motion.div>
  );
}

// ── Editing Suite animated showcase tile ────────────────────────────────────
const EDIT_TOOLS = [
  { label: "Video Upscale 4×",    detail: "Real-ESRGAN → Topaz Pro — 720p to crisp 4K" },
  { label: "Auto-Captions",       detail: "Wizper transcribes & burns styled subtitles onto video" },
  { label: "Reframe for Reels",   detail: "AI inpaints 16:9 footage into perfect 9:16 vertical" },
  { label: "Lip Sync",            detail: "Replace audio and sync mouth movement perfectly" },
  { label: "Background Removal",  detail: "Clean cutout from images and entire video clips" },
  { label: "AI Relight",          detail: "Change lighting with a prompt on any image" },
  { label: "Assemble Ad",         detail: "Captions + music → finished MP4 in one click" },
];

function ShowcaseEditingSuite({ tool, desc }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % EDIT_TOOLS.length), 2800);
    return () => clearInterval(id);
  }, []);
  const current = EDIT_TOOLS[idx];
  return (
    <div className="absolute inset-0 flex flex-col p-5 bg-gradient-to-br from-[#0d1220] to-[#080910] overflow-hidden" data-testid="showcase-editing-suite">
      {/* Animated grid lines bg */}
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "linear-gradient(#00e5ff 1px,transparent 1px),linear-gradient(90deg,#00e5ff 1px,transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="flex items-center justify-between gap-2 relative z-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary border border-primary/30">
          <Scissors className="h-3 w-3" /> {tool}
        </span>
        <span className="rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-[10px] font-semibold text-white/60">
          {idx + 1} / {EDIT_TOOLS.length}
        </span>
      </div>
      <div className="flex-1 flex flex-col justify-center relative z-10 mt-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-white">{current.label}</p>
            <p className="mt-2 text-[13px] leading-relaxed text-white/60">{current.detail}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-between gap-2 pt-3 relative z-10">
        <p className="text-sm font-semibold text-white/80">{desc}</p>
        <div className="flex gap-1 shrink-0">
          {EDIT_TOOLS.map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all ${i === idx ? "w-4 bg-primary" : "w-1.5 bg-white/20"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Manual Photo Editor showcase tile ───────────────────────────────────────
const MANUAL_FEATS = [
  "Crop & straighten — 40+ aspect ratio presets",
  "Text, shapes, arrows & freehand annotation",
  "Brightness · contrast · saturation · warmth · blur",
  "30+ cinematic & vintage filter presets",
  "Resize & export at any resolution",
  "Logo / watermark overlay at any position",
];

function ShowcaseManualEditor({ tool, desc }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((i) => (i + 1) % MANUAL_FEATS.length), 2600);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="absolute inset-0 flex flex-col p-5 bg-gradient-to-br from-[#131026] to-[#0a080f] overflow-hidden" data-testid="showcase-manual-editor">
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "radial-gradient(circle, #a78bfa 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
      <div className="flex items-center gap-2 relative z-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-400 border border-violet-400/30">
          <Crop className="h-3 w-3" /> {tool}
        </span>
      </div>
      <div className="flex-1 flex flex-col justify-center gap-2 mt-4 relative z-10">
        {MANUAL_FEATS.map((f, i) => (
          <motion.div
            key={i}
            animate={{ opacity: i === active ? 1 : 0.28, scale: i === active ? 1 : 0.97 }}
            transition={{ duration: 0.35 }}
            className={`flex items-center gap-2.5 rounded-lg px-3 py-2 transition-colors ${i === active ? "bg-violet-500/10 border border-violet-400/20" : ""}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${i === active ? "bg-violet-400" : "bg-white/20"}`} />
            <span className={`text-xs leading-snug ${i === active ? "text-white font-semibold" : "text-white/50"}`}>{f}</span>
          </motion.div>
        ))}
      </div>
      <p className="text-sm font-semibold text-white/80 relative z-10 pt-3">{desc}</p>
    </div>
  );
}

// ── Storyboard flow showcase tile ────────────────────────────────────────────
const SB_STEPS = [
  { step: "01", label: "Paste your script", icon: Type },
  { step: "02", label: "Auto-split into scenes", icon: Film },
  { step: "03", label: "AI generates each scene", icon: Sparkles },
  { step: "04", label: "Assembled into one video", icon: Video },
];

function ShowcaseStoryboard({ tool, desc }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((i) => (i + 1) % SB_STEPS.length), 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="absolute inset-0 flex flex-col p-5 bg-gradient-to-br from-[#0f1a14] to-[#080d0a] overflow-hidden" data-testid="showcase-storyboard">
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: "linear-gradient(#22c55e 1px,transparent 1px)", backgroundSize: "60px 60px" }} />
      <div className="flex items-center gap-2 relative z-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-400 border border-emerald-400/30">
          <Film className="h-3 w-3" /> {tool}
        </span>
      </div>
      <div className="flex-1 flex items-center relative z-10 mt-4">
        <div className="w-full">
          {SB_STEPS.map((s, i) => {
            const Icon = s.icon;
            const state = i < active ? "done" : i === active ? "active" : "pending";
            return (
              <div key={i} className="flex items-center gap-3 mb-3 last:mb-0">
                <motion.div
                  animate={{
                    backgroundColor: state === "done" ? "#22c55e33" : state === "active" ? "#22c55e20" : "rgba(255,255,255,0.04)",
                    borderColor: state === "done" ? "#22c55e" : state === "active" ? "#22c55e80" : "rgba(255,255,255,0.1)",
                  }}
                  className="h-8 w-8 rounded-lg border flex items-center justify-center shrink-0"
                >
                  <Icon className={`h-3.5 w-3.5 ${state !== "pending" ? "text-emerald-400" : "text-white/30"}`} />
                </motion.div>
                <div className="flex-1 min-w-0">
                  <motion.p
                    animate={{ opacity: state === "pending" ? 0.3 : 1 }}
                    className={`text-sm font-semibold ${state === "active" ? "text-white" : state === "done" ? "text-emerald-400" : "text-white/30"}`}
                  >
                    {s.step} — {s.label}
                  </motion.p>
                </div>
                {state === "done" && (
                  <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-emerald-400 text-xs font-bold">✓</motion.span>
                )}
                {state === "active" && (
                  <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1, repeat: Infinity }}
                    className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-sm font-semibold text-white/80 relative z-10 pt-3">{desc}</p>
    </div>
  );
}

// ── Toolkit category card ────────────────────────────────────────────────────
function ToolkitCategory({ icon: Icon, label, accent, border, bg, tools }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`rounded-2xl border ${border} ${bg} p-6`}
      data-testid={`toolkit-category-${label.toLowerCase()}`}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className={`h-9 w-9 rounded-xl ${bg} border ${border} flex items-center justify-center`}>
          <Icon className={`h-4.5 w-4.5 ${accent}`} style={{ height: "18px", width: "18px" }} />
        </div>
        <span className={`font-heading text-lg font-black tracking-tight ${accent}`}>{label}</span>
      </div>
      <ul className="space-y-2.5">
        {tools.map((t) => (
          <li key={t.name} className="flex items-start gap-2.5">
            <ChevronRight className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${accent} opacity-70`} />
            <div className="min-w-0">
              <span className="text-sm font-semibold text-white">{t.name}</span>
              {t.detail && (
                <p className="text-xs text-muted-foreground/70 leading-snug mt-0.5">{t.detail}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}
